import { useCallback, useEffect, useState } from "react";
import { GetAllAppVersions, SaveAppVersion } from "../../../utils/apiCalls/appVersion";
import { useToast } from "../../../utils/ToastContext";
import useDebouncedValue from "../../../hooks/useDebouncedValue";
import useLatestAppVersions from "../../../hooks/useLatestAppVersions";

const initialForm = { platform: "", releaseNotes: "" };

const useAppVersions = () => {
  const { showToast } = useToast();

  const [rows, setRows] = useState([]);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalRows, setTotalRows] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [fetchError, setFetchError] = useState("");

  const [searchQuery, setSearchQuery] = useState("");
  const debouncedSearchQuery = useDebouncedValue(searchQuery, 500);
  const [platformFilter, setPlatformFilter] = useState("");

  const { latestVersions, refetchLatestVersions } = useLatestAppVersions();

  const [addModalOpen, setAddModalOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [addForm, setAddForm] = useState(initialForm);
  const [errors, setErrors] = useState(initialForm);

  const fetchAllVersions = useCallback(
    async (pageIndex, pageSize, searchString, platform) => {
      setIsLoading(true);
      setFetchError("");
      const payload = {
        pageIndex,
        pageSize,
        searchString: searchString ?? "",
        ...(platform ? { platform: Number(platform) } : {}),
      };
      try {
        const response = await GetAllAppVersions(payload);
        if (response?.statusCode === 200) {
          setRows(response.data || []);
          setTotalRows(response.totalRecords || 0);
        } else {
          setRows([]);
          setFetchError(response?.message || "Failed to load app versions");
        }
      } catch (error) {
        setRows([]);
        setFetchError(error?.message || "Failed to load app versions");
      }
      setIsLoading(false);
    },
    [],
  );

  useEffect(() => {
    fetchAllVersions(page, rowsPerPage, debouncedSearchQuery, platformFilter);
  }, [fetchAllVersions, page, rowsPerPage, debouncedSearchQuery, platformFilter]);

  const retryFetchVersions = () => {
    fetchAllVersions(page, rowsPerPage, debouncedSearchQuery, platformFilter);
  };

  const handleSearchChange = (event) => {
    setSearchQuery(event.target.value);
    setPage(0);
  };

  const handlePlatformFilterChange = (event) => {
    setPlatformFilter(event.target.value);
    setPage(0);
  };

  const handleChangePage = (event, newPage) => setPage(newPage);

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const openAddModal = () => {
    setAddForm(initialForm);
    setErrors(initialForm);
    setAddModalOpen(true);
  };

  const closeAddModal = () => {
    setAddModalOpen(false);
    setAddForm(initialForm);
    setErrors(initialForm);
  };

  const handleChange = (eOrValue) => {
    const { name, value } = eOrValue?.target ?? eOrValue;
    setAddForm((prev) => ({ ...prev, [name]: value }));
    setErrors((prev) => ({ ...prev, [name]: "" }));
  };

  const validateForm = () => {
    const errObj = { ...initialForm };
    if (!addForm.platform) errObj.platform = "Please select a platform";
    if (!addForm.releaseNotes?.trim()) errObj.releaseNotes = "Release notes are required";
    setErrors(errObj);
    return Object.values(errObj).every((x) => !x);
  };

  const handleSubmit = async () => {
    if (!validateForm()) return;
    setIsSubmitting(true);
    try {
      const res = await SaveAppVersion({
        platform: Number(addForm.platform),
        releaseNotes: addForm.releaseNotes.trim(),
      });
      if (res?.statusCode === 200) {
        showToast(res.message || "App version saved successfully", "success");
        closeAddModal();
        setPage(0);
        fetchAllVersions(0, rowsPerPage, debouncedSearchQuery, platformFilter);
        refetchLatestVersions();
      } else {
        showToast(res?.message || "Failed to save app version", "error");
      }
    } catch (error) {
      showToast(error?.message || "Failed to save app version", "error");
    } finally {
      setIsSubmitting(false);
    }
  };

  return {
    rows,
    page,
    rowsPerPage,
    totalRows,
    isLoading,
    fetchError,
    searchQuery,
    platformFilter,
    latestVersions,
    addModalOpen,
    isSubmitting,
    addForm,
    errors,
    retryFetchVersions,
    handleSearchChange,
    handlePlatformFilterChange,
    handleChangePage,
    handleChangeRowsPerPage,
    openAddModal,
    closeAddModal,
    handleChange,
    handleSubmit,
  };
};

export default useAppVersions;
